import { useEffect, useState } from "react";
import API from "@/lib/api";
import { POST_TYPES } from "@/constants/site";
import { Megaphone, CalendarDays, FileText, Image as ImageIcon, Music, Flower2, Video } from "lucide-react";
import { useLang } from "@/lib/lang";

const ICONS = {
  image: ImageIcon,
  video: Video,
  megaphone: Megaphone,
  calendar: CalendarDays,
  file: FileText,
  music: Music,
  flower: Flower2,
};

export default function SearchOverlay({ open, onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const { lang, t } = useLang();

  useEffect(() => {
    if (!q.trim()) {
      setResults([]);
      return;
    }
    setBusy(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await API.get("/posts", { params: { q: q.trim(), limit: 8 } });
        setResults(data);
      } catch (e) {
        setResults([]);
      } finally {
        setBusy(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (!open) setQ("");
  }, [open]);

  const jump = () => {
    onClose();
    setTimeout(() => {
      if (window.__lenis) window.__lenis.scrollTo("#updates", { offset: -70 });
      else document.getElementById("updates")?.scrollIntoView({ behavior: "smooth" });
    }, 60);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center pt-24 px-4 bg-black/70" onClick={onClose} data-testid="search-overlay">
      <div
        className="w-full max-w-xl rounded-2xl bg-[#1E0A0E] border border-[#F2B134]/25 p-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          autoFocus
          data-testid="search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("search_ph")}
          className="w-full bg-transparent border-b border-[#F2B134]/30 focus:border-[#F2B134] outline-none py-3 text-lg text-[#FDF6EA] placeholder:text-[#D1BBA7]/50"
        />
        <div className="mt-3 max-h-80 overflow-y-auto no-scrollbar">
          {busy && <p className="text-sm text-[#D1BBA7]/70 py-3">{t("search_busy")}</p>}
          {!busy && q.trim() && results.length === 0 && (
            <p className="text-sm text-[#D1BBA7]/70 py-3">{t("search_empty")}</p>
          )}
          {results.map((p) => {
            const Icon = ICONS[POST_TYPES[p.type]?.icon] || Megaphone;
            return (
              <button
                key={p.id}
                data-testid="search-result-item"
                onClick={jump}
                className="w-full text-left flex items-start gap-3 rounded-xl px-3 py-3 hover:bg-[#F2B134]/10 transition-colors"
              >
                <span className="mt-0.5 h-8 w-8 shrink-0 rounded-lg bg-[#2B0F15] border border-[#F2B134]/25 grid place-items-center text-[#F2B134]">
                  <Icon size={15} />
                </span>
                <span>
                  <span className="block text-[#FDF6EA] font-semibold">{p.title}</span>
                  <span className="block text-xs text-[#D1BBA7]/70">{(lang === "en" ? POST_TYPES[p.type]?.labelEN : POST_TYPES[p.type]?.label) || p.type}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
